import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';
import { Check, ShieldCheck, X, Droplets } from 'lucide-react';

export const SealerSelector = ({ includeSealer, onSelect }) => {
  const { t, isRTL } = useLanguage();

  const options = [
    {
      id: true,
      titleHe: 'כן, הוסף סילר',
      titleEn: 'Yes, add sealer',
      descHe: 'הגנה מקסימלית מפני מים ולכלוך - מומלץ לשימוש חיצוני',
      descEn: 'Maximum protection from water and dirt - recommended for outdoor use',
      icon: ShieldCheck,
      emoji: '🛡️',
      recommended: true,
    },
    {
      id: false,
      titleHe: 'לא, ללא סילר',
      titleEn: 'No sealer needed',
      descHe: 'לשימוש פנימי בתנאים יבשים',
      descEn: 'For indoor use in dry conditions',
      icon: X,
      emoji: '🏠',
      recommended: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {options.map((option, index) => {
        const Icon = option.icon;
        const isSelected = includeSealer === option.id;

        return (
          <motion.button
            key={option.id.toString()}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4 }}
            whileHover={{ scale: 1.02, y: -4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelect(option.id)}
            className={cn(
              'relative p-8 rounded-3xl border-2 transition-all duration-300 text-start',
              isSelected 
                ? 'border-accent bg-accent/5 shadow-glow' 
                : 'border-border/40 hover:border-accent/30 hover:shadow-medium bg-card'
            )}
          >
            {isSelected && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-4 end-4 w-8 h-8 rounded-full bg-accent flex items-center justify-center shadow-lg"
              >
                <Check className="w-5 h-5 text-accent-foreground" />
              </motion.div>
            )}

            {option.recommended && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-4 start-4"
              >
                <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-success/10 text-success border border-success/20">
                  {t('recommended')}
                </span>
              </motion.div>
            )}

            <div className="flex items-start gap-5 mt-6">
              <motion.div 
                className={cn(
                  'w-20 h-20 rounded-2xl flex items-center justify-center text-4xl transition-all duration-300',
                  isSelected 
                    ? option.id ? 'bg-success/10' : 'bg-secondary'
                    : 'bg-secondary'
                )}
                whileHover={{ scale: 1.1 }}
              >
                {option.emoji}
              </motion.div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {isRTL ? option.titleHe : option.titleEn}
                </h3>
                <p className="text-muted-foreground">
                  {isRTL ? option.descHe : option.descEn}
                </p>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};
